import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

type Store<T> = Record<string, Record<string, T>>;

export const useMediaCooldown = <T extends { lastAt: number }>(
  storageKey: string,
  getDefault: () => T = () => ({ lastAt: Date.now() }) as T
) => {
  const [, setData] = useLocalStorage<Store<T>>(storageKey, {});

  const trigger = useCallback(
    (id: string, data?: T | ((prev: T | undefined) => T)) => {
      if (!id) return;

      setData((prevStore) => {
        const bucket = prevStore[storageKey] ?? {};
        const prevValue = bucket[id];

        const nextValue =
          typeof data === 'function'
            ? (data as (p: T | undefined) => T)(prevValue)
            : (data ?? getDefault());

        return {
          ...prevStore,
          [storageKey]: {
            ...bucket,
            [id]: nextValue,
          },
        };
      });
    },
    [storageKey, setData, getDefault]
  );

  return { trigger };
};
